import { useEffect, useMemo, useState } from 'react'
import { Direction, RelationType } from 'matrix-js-sdk'
import { requireClient } from '@/lib/matrix/client'
import { serializeEvent, type SerializedEvent } from '@/lib/matrix/events'
import { useTimelineStore, selectTimeline } from '@/lib/store/timeline-store'
import { useAuthStore } from '@/lib/store/auth-store'
import { MessageBubble } from './MessageBubble'
import { MessageComposer } from '@/components/composer/MessageComposer'

interface ThreadPanelProps {
  roomId: string
  rootEvent: SerializedEvent
  onClose: () => void
}

function isReplyTo(e: SerializedEvent, rootId: string): boolean {
  const r = e.content['m.relates_to'] as Record<string, unknown> | undefined
  return r?.rel_type === 'm.thread' && r?.event_id === rootId
}

export function ThreadPanel({ roomId, rootEvent, onClose }: ThreadPanelProps) {
  const userId = useAuthStore((s) => s.userId)
  const allEvents = useTimelineStore(selectTimeline(roomId))
  const [fetched, setFetched] = useState<SerializedEvent[]>([])

  // Historical thread replies (the live timeline may only hold recent ones).
  useEffect(() => {
    let cancelled = false
    requireClient()
      .relations(roomId, rootEvent.eventId, RelationType.Thread, null, { dir: Direction.Forward })
      .then((res) => {
        if (!cancelled) setFetched(res.events.map(serializeEvent))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [roomId, rootEvent.eventId])

  // Replies that have arrived live, plus the fetched history, de-duped + sorted.
  const replies = useMemo(() => {
    const live = allEvents.filter((e) => isReplyTo(e, rootEvent.eventId))
    const map = new Map<string, SerializedEvent>()
    for (const e of [...fetched, ...live]) map.set(e.eventId, e)
    return [...map.values()].sort((a, b) => a.timestamp - b.timestamp)
  }, [allEvents, fetched, rootEvent.eventId])

  const noop = () => {}

  return (
    <aside className="w-96 max-w-[80vw] border-l border-[rgb(var(--np-border))] bg-[rgb(var(--np-surface))] flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[rgb(var(--np-border))]">
        <span className="label-section">Thread</span>
        <button
          onClick={onClose}
          className="p-1 text-[rgb(var(--np-tx-muted))] hover:text-[rgb(var(--np-tx-primary))] transition-colors"
          title="Close thread"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
        <MessageBubble
          event={rootEvent}
          isOwn={rootEvent.sender === userId}
          isGrouped={false}
          roomId={roomId}
          onReply={noop}
          onEdit={noop}
          onReact={noop}
        />
        <div className="flex items-center gap-2 py-1">
          <div className="flex-1 h-px bg-[rgb(var(--np-border))]" />
          <span className="text-[10px] uppercase tracking-wider text-[rgb(var(--np-tx-muted))]">
            {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
          </span>
          <div className="flex-1 h-px bg-[rgb(var(--np-border))]" />
        </div>
        {replies.map((event) => (
          <MessageBubble
            key={event.eventId}
            event={event}
            isOwn={event.sender === userId}
            isGrouped={false}
            roomId={roomId}
            onReply={noop}
            onEdit={noop}
            onReact={noop}
          />
        ))}
      </div>

      <MessageComposer
        roomId={roomId}
        replyTo={null}
        editingEvent={null}
        threadRootId={rootEvent.eventId}
        onCancelReply={noop}
        onCancelEdit={noop}
      />
    </aside>
  )
}
